/**
 * Priority Toggle Menu Component
 * Dropdown menu for enabling/disabling priority mode
 * Matches admin upload button style
 * Supports hover-open mode (no backdrop) and click-open mode (with backdrop)
 */

interface PriorityToggleMenuProps {
  isOpen: boolean
  onClose: () => void
  priorityEnabled: boolean
  onToggle: (enabled: boolean) => void
  onTipClick: () => void
  showBackdrop?: boolean // Only show backdrop when opened via click
}

export function PriorityToggleMenu({
  isOpen,
  onClose,
  priorityEnabled,
  onToggle,
  onTipClick,
  showBackdrop = true
}: PriorityToggleMenuProps) {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop - only shown when opened via click */}
      {showBackdrop && (
        <div
          className="fixed inset-0 z-40"
          onClick={onClose}
        />
      )}

      {/* Dropdown Menu */}
      <div className={`
        absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200
        min-w-[200px] overflow-hidden z-50
        transform transition-all duration-300 origin-bottom-left
        animate-fade-in-scale
      `}>
        {/* Tip Option */}
        <button
          onClick={() => {
            onTipClick()
            onClose()
          }}
          className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-gray-50 transition-colors"
        >
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium text-gray-900">Send Tip</span>
        </button>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Priority Message Option */}
        <button
          onClick={() => {
            onToggle(!priorityEnabled)
            onClose()
          }}
          className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors ${
            priorityEnabled
              ? 'bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100'
              : 'hover:bg-gray-50'
          }`}
        >
          <svg className={`w-5 h-5 ${priorityEnabled ? 'text-amber-600' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className={`text-sm font-medium ${priorityEnabled ? 'text-amber-900' : 'text-gray-900'}`}>
            Buzz Mini's Phone
          </span>
        </button>
      </div>

      {/* Add fade-in scale animation */}
      <style>{`
        @keyframes fade-in-scale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(5px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fade-in-scale {
          animation: fade-in-scale 0.2s ease-out;
        }
      `}</style>
    </>
  )
}
